import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useFontSize } from '../../contexts/FontSizeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getPageTitleSizeClass, getSectionHeadingSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';
import { sessionSaveService } from '../../services/sessionSaveService';

const GetStarted: React.FC = () => {
  const { fontSize } = useFontSize();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    document.title = 'Stock Table - Get Started';
    window.scrollTo(0, 0);
  }, []);

  const handleTryNow = () => {
    // Navigate to the daily page for guest access with state to show promotional tooltip
    navigate('/daily', {
      state: { fromGetStarted: true }
    });
    // Scroll to top after navigation (small delay to ensure page has rendered)
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  const handleRegisterFree = () => {
    // Try to use sessionSaveService first (like Header Sign-Up button does)
    // This ensures we get fresh, properly formatted data
    let sessionSaved = false;
    const sessionData = sessionSaveService.getSessionData();
    
    if (sessionData && sessionData.tickerList && sessionData.tickerList.length > 0) {
      sessionSaved = sessionSaveService.saveCurrentSessionToStorage();
    } else {
      // Fall back to sessionStorage (user might have navigated away from list page)
      const tickerListStr = sessionStorage.getItem('stocktable-ticker-list');
      const listName = sessionStorage.getItem('stocktable-list-name');
      
      if (tickerListStr) {
        try {
          const tickerList = JSON.parse(tickerListStr);
          if (Array.isArray(tickerList) && tickerList.length > 0) {
            // Ensure data is properly formatted in sessionStorage (consistent with Save button)
            const finalListName = listName || 'Main List';
            sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(tickerList));
            sessionStorage.setItem('stocktable-list-name', finalListName);
            
            // Note: hiddenStatistics should already be in sessionStorage from list pages
            // We don't need to modify it, just ensure it's preserved
            
            sessionSaved = true;
          }
        } catch (e) {
          console.error('Failed to parse ticker list from sessionStorage:', e);
        }
      }
    }
    
    if (sessionSaved) {
      navigate('/signup', {
        state: {
          fromSignUp: true,
          returnPath: '/daily',
        },
      });
    } else {
      navigate('/signup');
    }
    
    // Scroll to top after navigation (small delay to ensure page has rendered)
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  const handleProButtonClick = () => {
    if (isAuthenticated && !user?.hasProSubscription) {
      navigate('/order-summary', {
        state: { freeTrialEligible: !user?.freeTrialUsedAt },
      });
      return;
    }
    handleSubscribeNowRedirect();
  };

  const handleSubscribeNowRedirect = () => {
    let sessionSaved = false;
    const sessionData = sessionSaveService.getSessionData();

    if (sessionData && sessionData.tickerList && sessionData.tickerList.length > 0) {
      sessionSaved = sessionSaveService.saveCurrentSessionToStorage();
    } else {
      const tickerListStr = sessionStorage.getItem('stocktable-ticker-list');
      const listName = sessionStorage.getItem('stocktable-list-name');

      if (tickerListStr) {
        try {
          const tickerList = JSON.parse(tickerListStr);
          if (Array.isArray(tickerList) && tickerList.length > 0) {
            const finalListName = listName || 'Main List';
            sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(tickerList));
            sessionStorage.setItem('stocktable-list-name', finalListName);
            sessionSaved = true;
          }
        } catch (e) {
          console.error('Failed to parse ticker list from sessionStorage:', e);
        }
      }
    }

    if (sessionSaved) {
      navigate('/signup', {
        state: { fromSignUp: true, returnPath: '/daily', fromFreeTrialIntent: true },
      });
    } else {
      navigate('/signup', { state: { fromFreeTrialIntent: true } });
    }
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
      <Header />
      
      <main className="flex-grow flex flex-col items-center justify-center px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto mb-12">
          <h1 className={`${getPageTitleSizeClass(fontSize)} md:text-5xl font-bold text-text-light-primary dark:text-text-primary mb-6 tracking-tight`}>
            Get Started Options
          </h1>
          <p className={`text-lg md:text-xl text-text-light-secondary dark:text-text-secondary leading-relaxed ${getTextSizeClass(fontSize)}`}>
            Compare our plans or try the service instantly without registering.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl w-full">
          {/* Try Without Registering */}
          <div className="bg-surface-light dark:bg-background-card rounded-xl shadow-lg border border-border-light dark:border-border-color flex flex-col relative overflow-hidden transition-transform hover:-translate-y-1 duration-300">
            <div className="p-6 xl:p-8 flex-grow">
              <h3 className={`${getSectionHeadingSizeClass(fontSize)} text-2xl font-bold text-text-light-primary dark:text-text-primary mb-2`}>
                No Registration
              </h3>
              <div className="flex items-baseline mb-6">
                <span className="text-3xl xl:text-4xl font-extrabold text-text-light-primary dark:text-text-primary">Guest</span>
                <span className={`ml-2 text-text-light-secondary dark:text-text-secondary font-medium ${getTextSizeClass(fontSize)}`}>
                  / session
                </span>
              </div>
              <p className={`text-text-light-secondary dark:text-text-secondary mb-8 ${getTextSizeClass(fontSize)}`}>
                Instant access to basic functionality. No account required.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-gray-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Only <span className="font-bold">1 list</span>
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-gray-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Track up to <span className="font-bold">10 total tickers</span>
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-gray-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Limited set of basic indicators
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-orange-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary text-sm font-medium text-orange-600 dark:text-orange-400 ${getTextSizeClass(fontSize)}`}>
                    Data resets when browser session ends
                  </span>
                </li>
              </ul>
            </div>
            {!isAuthenticated && (
              <div className="p-6 xl:p-8 pt-0 mt-auto">
                <button
                  onClick={handleTryNow}
                  className={`w-full py-3 px-6 border-2 border-border-light dark:border-border-color hover:border-gray-400 dark:hover:border-gray-500 text-text-light-primary dark:text-text-primary font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:focus:ring-offset-gray-900 ${getTextSizeClass(fontSize)}`}
                >
                  Try Now
                </button>
              </div>
            )}
          </div>

          {/* Free Plan */}
          <div className="bg-surface-light dark:bg-background-card rounded-xl shadow-lg border border-border-light dark:border-border-color flex flex-col relative overflow-hidden transition-transform hover:-translate-y-1 duration-300">
            <div className="p-6 xl:p-8 flex-grow">
              <h3 className={`${getSectionHeadingSizeClass(fontSize)} text-2xl font-bold text-text-light-primary dark:text-text-primary mb-2`}>
                Free Plan
              </h3>
              <div className="flex items-baseline mb-6">
                <span className="text-3xl xl:text-4xl font-extrabold text-text-light-primary dark:text-text-primary">$0</span>
                <span className={`ml-2 text-text-light-secondary dark:text-text-secondary font-medium ${getTextSizeClass(fontSize)}`}>
                  / forever
                </span>
              </div>
              <p className={`text-text-light-secondary dark:text-text-secondary mb-8 ${getTextSizeClass(fontSize)}`}>
              Perfect for casual investors getting started with side-by-side comparison.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Up to <span className="font-bold">3 lists</span>
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Up to <span className="font-bold">10 tickers</span> per list
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Track up to <span className="font-bold">20 total tickers</span>
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Limited set of basic indicators
                  </span>
                </li>
                <li className="flex items-start opacity-50">
                  <svg className="w-6 h-6 text-text-light-secondary dark:text-text-secondary mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-secondary dark:text-text-secondary ${getTextSizeClass(fontSize)}`}>
                    Advanced technicals & fundamentals
                  </span>
                </li>
              </ul>
            </div>
            {!(isAuthenticated && user?.hasProSubscription) && (
              <div className="p-6 xl:p-8 pt-0 mt-auto">
                <button
                  onClick={handleRegisterFree}
                  disabled={isAuthenticated && !user?.hasProSubscription}
                  className={`w-full py-3 px-6 border-2 border-border-light dark:border-border-color ${
                    isAuthenticated && !user?.hasProSubscription
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:border-primary dark:hover:border-highlight-gold'
                  } text-text-light-primary dark:text-text-primary font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-highlight-gold dark:focus:ring-offset-gray-900 ${getTextSizeClass(fontSize)}`}
                >
                  {isAuthenticated && !user?.hasProSubscription ? 'Your Current Plan' : 'Register Free'}
                </button>
              </div>
            )}
          </div>

          {/* Pro Plan */}
          <div className="bg-surface-light dark:bg-background-card rounded-xl shadow-xl border-2 border-primary dark:border-highlight-gold relative flex flex-col overflow-hidden transition-transform hover:-translate-y-1 duration-300">
            <div className="absolute top-0 right-0 bg-primary dark:bg-highlight-gold text-gray-900 text-xs font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">
              Most Popular
            </div>
            <div className="p-6 xl:p-8 flex-grow">
              <h3 className={`${getSectionHeadingSizeClass(fontSize)} text-2xl font-bold text-text-light-primary dark:text-text-primary mb-2`}>
                Pro Plan
              </h3>
              <div className="flex items-baseline mb-6">
                <span className="text-3xl xl:text-4xl font-extrabold text-text-light-primary dark:text-text-primary">US$4.99</span>
                <span className={`ml-2 text-text-light-secondary dark:text-text-secondary font-medium ${getTextSizeClass(fontSize)}`}>
                  / month
                </span>
              </div>
              <p className={`text-text-light-secondary dark:text-text-secondary mb-8 ${getTextSizeClass(fontSize)}`}>
              For serious investors who need deep insights and unlimited flexibility.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary dark:text-highlight-gold mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Up to <span className="font-bold">10 lists</span>
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary dark:text-highlight-gold mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Up to <span className="font-bold">20 tickers</span> per list
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary dark:text-highlight-gold mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)}`}>
                    Track up to <span className="font-bold">100 total tickers</span>
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-primary dark:text-highlight-gold mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-text-light-primary dark:text-text-primary font-medium ${getTextSizeClass(fontSize)}`}>
                    All Premium Indicators:
                  </span>
                </li>
                <li className="flex items-start pl-8">
                  <ul className="list-disc space-y-1 text-text-light-secondary dark:text-text-secondary">
                    <li className={getTextSizeClass(fontSize)}>Fibonacci Levels & MACD</li>
                    <li className={getTextSizeClass(fontSize)}>EV/FCF Ratio, ROIC & Operating Cash Flow Ratio</li>
                    <li className={getTextSizeClass(fontSize)}>Free Cash Flow Yield, Cash Flow Margin</li>
                    <li className={getTextSizeClass(fontSize)}>Pros & Cons for Investing</li>
                  </ul>
                </li>
              </ul>
            </div>
            <div className="p-6 xl:p-8 pt-0 mt-auto">
              <button
                onClick={handleProButtonClick}
                disabled={isAuthenticated && user?.hasProSubscription}
                className={`w-full py-3 px-6 bg-primary dark:bg-highlight-gold ${
                  isAuthenticated && user?.hasProSubscription
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:opacity-90'
                } text-gray-900 font-bold rounded-lg shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-highlight-gold dark:focus:ring-offset-gray-900 ${getTextSizeClass(fontSize)}`}
              >
                {isAuthenticated && user?.hasProSubscription
                  ? 'Your Current Plan'
                  : !isAuthenticated || !user?.freeTrialUsedAt
                    ? 'Start Free Trial Now'
                    : 'Subscribe Now'}
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GetStarted;

